import { useState, useRef, useCallback } from "react"
import { Trans, useTranslation } from "react-i18next"
import { ImagePlus, X, Loader2, AlertCircle, Info } from "lucide-react"
import { toast } from "sonner"
import { api } from "@/lib/api"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { ReferenceImage } from "@/types/generation"
import { downscaleImageIfNeeded } from "@/lib/image-downscale"
import { useAuthMe } from "@/hooks/useAuthMe"

/** 업로드 전 축소 기준: 긴 변 2048px, 5MB (서버 상한과 동일) */
const MAX_IMAGE_EDGE = 2048
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024

interface ReferenceImageUploadProps {
  images: ReferenceImage[]
  onImagesChange: (images: ReferenceImage[]) => void
  maxImages?: number
  disabled?: boolean
  /** 이미지 생성을 한 번이라도 했는지 (맥락 안내용) */
  hasGeneratedOnce?: boolean
  /** 맥락 안내 dismiss 콜백 */
  onDismissHint?: () => void
}

/**
 * 레퍼런스 이미지 업로드 컴포넌트
 * 최대 4개의 이미지를 업로드할 수 있으며, 4컬럼 그리드로 표시됩니다.
 */
export function ReferenceImageUpload({
  images,
  onImagesChange,
  maxImages = 4,
  disabled = false,
  hasGeneratedOnce = false,
  onDismissHint,
}: ReferenceImageUploadProps) {
  const { t } = useTranslation()
  const isDrmActive = useAuthMe().data?.isDrmActive === true
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || disabled) return

      // 현재 이미지 수 + 새로 선택한 파일 수가 최대값을 초과하는지 확인
      const remainingSlots = maxImages - images.length
      if (remainingSlots <= 0) {
        toast.error(t("generator.uploadLimitTitle"), {
          description: t("generator.uploadLimitDescription", { max: maxImages }),
        })
        return
      }

      const filesToUpload = Array.from(files).slice(0, remainingSlots)

      setIsUploading(true)
      setUploadError(null)

      try {
        // 큰 사진은 업로드 상한(5MB)에 걸리기 전에 브라우저에서 줄인다. 서버도 원본을 1024px로
        // 리사이즈하므로 화질 손실은 사실상 없다. DRM 테넌트의 암호화 파일은 브라우저가 디코딩할
        // 수 없어 원본을 그대로 보내고 서버가 복호화한다.
        const prepared = isDrmActive
          ? filesToUpload
          : await Promise.all(
              filesToUpload.map((file) =>
                downscaleImageIfNeeded(file, { maxEdge: MAX_IMAGE_EDGE, maxBytes: MAX_UPLOAD_BYTES })
              )
            )

        // multipart/form-data 업로드
        const formData = new FormData()
        for (const file of prepared) {
          formData.append('files', file, file.name)
        }

        const uploadedImages = await api.post<ReferenceImage[]>(
          "/generate/reference-images",
          formData
        )

        onImagesChange([...images, ...uploadedImages])
        toast.success(t("generator.uploadSuccessTitle"), {
          description: t("generator.uploadSuccessDescription", {
            count: uploadedImages.length,
          }),
        })
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t("generator.uploadFailedFallback")
        setUploadError(message)
        toast.error(t("generator.uploadFailedTitle"), { description: message })
      } finally {
        setIsUploading(false)
        // 파일 입력 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      }
    },
    [images, maxImages, onImagesChange, disabled, isDrmActive, t]
  )

  const handleRemoveImage = useCallback(
    (imageId: string) => {
      onImagesChange(images.filter((img) => img.id !== imageId))
    },
    [images, onImagesChange]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      handleFileSelect(e.dataTransfer.files)
    },
    [handleFileSelect]
  )

  const canAddMore = images.length < maxImages && !disabled

  return (
    <div className="space-y-3">
      {/* 에러 메시지 */}
      {uploadError && (
        <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-md p-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {uploadError}
        </div>
      )}

      {/* 4컬럼 그리드 - 모바일에서 gap 축소 */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {/* 업로드된 이미지들 */}
        {images.map((image) => (
          <div
            key={image.id}
            className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden group"
          >
            <img
              src={image.thumbnailUrl || image.url}
              alt={t("generator.referenceImage")}
              className="w-full h-full object-cover"
            />
            {/* 삭제 버튼 */}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemoveImage(image.id)}
                className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}

        {/* 빈 슬롯들 (placeholder) */}
        {Array.from({ length: maxImages - images.length }).map((_, index) => {
          const isFirstSlot = canAddMore && index === 0
          const slotContent = (
            <div
              key={`empty-${index}`}
              className={`aspect-square rounded-lg border-2 border-dashed transition-colors flex items-center justify-center ${
                isFirstSlot
                  ? "border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                  : "border-slate-200 bg-slate-50"
              }`}
              onClick={isFirstSlot ? () => fileInputRef.current?.click() : undefined}
              onDragOver={isFirstSlot ? handleDragOver : undefined}
              onDrop={isFirstSlot ? handleDrop : undefined}
            >
              {isFirstSlot ? (
                isUploading ? (
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-slate-400" />
                )
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
              )}
            </div>
          )

          if (isFirstSlot && !isUploading) {
            return (
              <TooltipProvider key={`empty-${index}`} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {slotContent}
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t("generator.uploadTooltip")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          }

          return slotContent
        })}
      </div>

      {/* 맥락 안내: 생성 후 레퍼런스가 비어있을 때 */}
      {images.length === 0 && hasGeneratedOnce && (
        <div className="relative flex items-start gap-2 text-sm text-primary bg-primary/5 border border-primary/20 rounded-md p-3">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">{t("generator.editHintTitle")}</p>
            <p className="text-xs text-slate-600 mt-0.5">
              {t("generator.editHintDescription")}
            </p>
          </div>
          {onDismissHint && (
            <button
              type="button"
              onClick={onDismissHint}
              className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
            >
              <X className="w-3 h-3 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {/* 상시 안내: 레퍼런스가 비어있을 때 */}
      {images.length === 0 && !hasGeneratedOnce && (
        <p className="text-xs text-slate-400 text-center">
          <Trans
            i18nKey="generator.emptyReferenceHint"
            components={{ bold: <span className="font-semibold" /> }}
          />
        </p>
      )}

      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || isUploading}
      />
    </div>
  )
}
